import { Rating } from "react-simple-star-rating";
import { LABELS } from "../../../shared/constants";
import { dateAndTimeConvert } from "../../../shared/helperFunc/dateConvert";
import { FeedbackDetailItem } from "../../../Features/customerFeedback/customer-feedback-slice";

interface CustomerFeedbackDetailProps {
  feedbackDetailData: FeedbackDetailItem | undefined;
  loading: boolean;
}

const getStarColor = (rating: number) => {
  if (rating <= 1) return "#EF4444"; // 1 star: Dull Red / Orange
  if (rating <= 2) return "#F97316"; // 2 stars: Orange
  if (rating <= 3) return "#F59E0B"; // 3 stars: Medium Amber
  if (rating <= 4) return "#EAB308"; // 4 stars: Bright Gold
  return "#FFC107"; // 5 stars: Full Bright Golden Yellow
};

const getBadgeStyle = (rating: number) => {
  if (rating <= 1)
    return {
      backgroundColor: "#FEF2F2",
      color: "#DC2626",
      border: "1px solid #FCA5A5",
    };
  if (rating <= 2)
    return {
      backgroundColor: "#FFF7ED",
      color: "#EA580C",
      border: "1px solid #FDBA74",
    };
  if (rating <= 3)
    return {
      backgroundColor: "#FEFCE8",
      color: "#CA8A04",
      border: "1px solid #FDE047",
    };
  if (rating <= 4)
    return {
      backgroundColor: "#FEFCE8",
      color: "#CA8A04",
      border: "1px solid #FDE047",
    };
  return {
    backgroundColor: "#F0FDF4",
    color: "#16A34A",
    border: "1px solid #86EFAC",
  };
};

// Read-only detail panel (contract #5). No mutation hooks, no edit/delete
// controls — submitted feedback is final.
const CustomerFeedbackDetail = (props: CustomerFeedbackDetailProps) => {
  const { feedbackDetailData, loading } = props;

  if (loading || !feedbackDetailData) {
    return <div className="text-center py-5 text-muted">Loading…</div>;
  }

  const submittedBy = feedbackDetailData.submittedBy;
  const operator =
    submittedBy?.name || submittedBy?.username || submittedBy?._id || "-";

  return (
    <div className="feedback-detail py-2">
      {/* Header Info Cards */}
      <div
        className="rounded-3 p-3 mb-4"
        style={{
          backgroundColor: "#FAF8F5",
          border: "1px solid #E8E2D8",
          boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
        }}
      >
        <div className="row g-3">
          <div className="col-12 col-sm-6 col-md-3">
            <div className="detail-field">
              <span
                className="detail-label d-block mb-1"
                style={{
                  fontFamily: "'Twentieth Century Bold', sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "#3E2215",
                }}
              >
                {LABELS.ORDER_NO_TEXT}:
              </span>
              <span
                className="detail-value d-block"
                style={{
                  fontFamily: "'Twentieth Century', sans-serif",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "#4A4A4A",
                }}
              >
                {feedbackDetailData.orderNumber || "-"}
              </span>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-3">
            <div className="detail-field">
              <span
                className="detail-label d-block mb-1"
                style={{
                  fontFamily: "'Twentieth Century Bold', sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "#3E2215",
                }}
              >
                {LABELS.STORE_NAME_TEXT}:
              </span>
              <span
                className="detail-value d-block"
                style={{
                  fontFamily: "'Twentieth Century', sans-serif",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "#4A4A4A",
                }}
              >
                {feedbackDetailData.storeName || "-"}
              </span>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-3">
            <div className="detail-field">
              <span
                className="detail-label d-block mb-1"
                style={{
                  fontFamily: "'Twentieth Century Bold', sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "#3E2215",
                }}
              >
                {LABELS.OPERATOR_TEXT}:
              </span>
              <span
                className="detail-value d-block"
                style={{
                  fontFamily: "'Twentieth Century', sans-serif",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "#4A4A4A",
                }}
              >
                {operator}
              </span>
            </div>
          </div>
          <div className="col-12 col-sm-6 col-md-3">
            <div className="detail-field">
              <span
                className="detail-label d-block mb-1"
                style={{
                  fontFamily: "'Twentieth Century Bold', sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "#3E2215",
                }}
              >
                {LABELS.SUBMITTED_AT_TEXT}:
              </span>
              <span
                className="detail-value d-block"
                style={{
                  fontFamily: "'Twentieth Century', sans-serif",
                  fontWeight: 400,
                  fontSize: "16px",
                  color: "#4A4A4A",
                }}
              >
                {dateAndTimeConvert(feedbackDetailData.submittedAt)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Ratings Section */}
      {feedbackDetailData.ratings.length > 0 && (
        <div className="mb-4">
          <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
            <h6
              className="mb-0"
              style={{
                fontFamily: "'Twentieth Century Bold', sans-serif",
                fontWeight: 700,
                fontSize: "18px",
                color: "#3E2215",
              }}
            >
              {LABELS.RATINGS_TEXT}
            </h6>
          </div>
          <div className="d-flex flex-column gap-2">
            {feedbackDetailData.ratings.map((rating) => (
              <div
                key={rating.questionId}
                className="d-flex align-items-center justify-content-between p-3 rounded-2"
                style={{
                  backgroundColor: "#FFFFFF",
                  border: "1px solid #EAEAEA",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
                }}
              >
                <span
                  style={{
                    fontFamily: "'Twentieth Century', sans-serif",
                    fontWeight: 400,
                    fontSize: "16px",
                    color: "#2D2D2D",
                  }}
                >
                  {rating.questionText}
                </span>
                <div className="d-flex align-items-center gap-2">
                  <Rating
                    readonly
                    initialValue={rating.rating}
                    size={22}
                    fillColor={getStarColor(rating.rating)}
                    emptyColor="#e5e7eb"
                  />
                  <span
                    className="badge rounded-pill ms-1"
                    style={{
                      ...getBadgeStyle(rating.rating),
                      fontSize: "13px",
                      fontWeight: 600,
                    }}
                  >
                    {rating.rating} / 5
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Comments Section */}
      {feedbackDetailData.comments.length > 0 && (
        <div className="mb-2">
          <div className="d-flex align-items-center gap-2 mb-3 pb-2 border-bottom">
            <h6
              className="mb-0"
              style={{
                fontFamily: "'Twentieth Century Bold', sans-serif",
                fontWeight: 700,
                fontSize: "18px",
                color: "#3E2215",
              }}
            >
              {LABELS.COMMENTS_TEXT}
            </h6>
          </div>
          <div className="d-flex flex-column gap-3">
            {feedbackDetailData.comments.map((comment) => (
              <div
                key={comment.commentFieldId}
                className="p-3 rounded-2"
                style={{
                  backgroundColor: "#F9FAFB",
                  border: "1px solid #EAECF0",
                  borderLeft: "4px solid #3E2215",
                }}
              >
                <div
                  className="mb-1 text-uppercase tracking-wide"
                  style={{
                    fontFamily: "'Twentieth Century Bold', sans-serif",
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#3E2215",
                  }}
                >
                  {comment.fieldLabel}
                </div>
                <div
                  className="detail-value word-break"
                  style={{
                    fontFamily: "'Twentieth Century', sans-serif",
                    fontWeight: 400,
                    fontSize: "16px",
                    color: "#333333",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {comment.commentText}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerFeedbackDetail;
